import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  MembersTableSortBy,
  MembersTableSortDirection,
} from '@/store/api/userApi';
import type { SelectChangeEvent } from '@/ui';
import type { ChangeEvent } from 'react';

const PAGE_KEY = 'page';
const LIMIT_KEY = 'limit';
const SEARCH_KEY = 'search';
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;
const MEMBERS_PAGE_SIZE_STORAGE_KEY = 'company_members_page_size';

const validLimits = [10, 20, 50, 100];

interface UseMembersTableStateResult {
  handleLimitChange: (event: SelectChangeEvent<number>) => void;
  handlePageChange: (_: ChangeEvent<unknown>, value: number) => void;
  handleSort: (field: MembersTableSortBy) => void;
  limit: number;
  page: number;
  search: string;
  setSearch: (value: string) => void;
  sortDirection: MembersTableSortDirection;
  sortField: MembersTableSortBy;
  validLimits: readonly number[];
}

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return parsed;
};

const getStoredLimit = () => {
  const raw = localStorage.getItem(MEMBERS_PAGE_SIZE_STORAGE_KEY);
  return parsePositiveInt(raw, NaN);
};

export const useMembersTableState = (): UseMembersTableStateResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sortField, setSortField] = useState<MembersTableSortBy>('name');
  const [sortDirection, setSortDirection] =
    useState<MembersTableSortDirection>('asc');

  const page = parsePositiveInt(searchParams.get(PAGE_KEY), DEFAULT_PAGE);
  const limitFromUrl = parsePositiveInt(
    searchParams.get(LIMIT_KEY) || `${getStoredLimit()}`,
    DEFAULT_LIMIT,
  );
  const limit = validLimits.includes(limitFromUrl)
    ? limitFromUrl
    : DEFAULT_LIMIT;
  const search = searchParams.get(SEARCH_KEY) ?? '';

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        const trimmedValue = value.trim();
        if (trimmedValue) {
          nextSearchParams.set(SEARCH_KEY, trimmedValue);
        } else {
          nextSearchParams.delete(SEARCH_KEY);
        }
        nextSearchParams.set(PAGE_KEY, `${DEFAULT_PAGE}`);
        return nextSearchParams;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (_: ChangeEvent<unknown>, value: number) => {
      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.set(PAGE_KEY, value.toString());
        return nextSearchParams;
      });
    },
    [setSearchParams],
  );

  const handleLimitChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newLimit = event.target.value as number;
      localStorage.setItem(MEMBERS_PAGE_SIZE_STORAGE_KEY, String(newLimit));

      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.set(LIMIT_KEY, newLimit.toString());
        nextSearchParams.set(PAGE_KEY, `${DEFAULT_PAGE}`);
        return nextSearchParams;
      });
    },
    [setSearchParams],
  );

  const handleSort = useCallback(
    (field: MembersTableSortBy) => {
      if (field === sortField) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }

      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.set(PAGE_KEY, `${DEFAULT_PAGE}`);
        return nextSearchParams;
      });
    },
    [setSearchParams, sortDirection, sortField],
  );

  return {
    handleLimitChange,
    handlePageChange,
    handleSort,
    limit,
    page,
    search,
    setSearch,
    sortDirection,
    sortField,
    validLimits,
  };
};
